import { safeRuleLabel } from "./fixture-placement-rules.js";
//#region src/deprecated-surface-rules.ts
/**
* The deprecation inventory as data, and the rules that read it (2026-09-04-app-pools, L-12,
* I-9; 2026-10-10-app-pools-contract-release, D-9 and D-13). Every path the audit found superseded
* by an app pool is one row here. The publish doors run `checkDeprecatedSurfaces` (warnings, both
* doors). Release-engine also runs `checkDeprecatedSurfaceNewUse` (errors, the CP door only): it
* refuses a surface that no earlier version of the same app declared. Both doors also run
* `checkRemovedSurfaces` (errors). The schema-marker test (WS-8 D-1) asserts every pointer carries
* `deprecated: true` in its JSON Schema; the census (I-7) walks the same pointers. One table, five
* readers: the three rule functions, the schema-marker test and the census. So the inventory cannot
* drift between them.
*
* The 40.0.0 major (2026-10-14-app-pools-runtime-deletion, R2, R-081, R-083) removed 14 of the 27
* rows with their schema keys: the seven `runtime: 'removed'` rows, the five compute-unit rows and
* the two `sizeClass` rows (`research/r2-manifest.md` §1 to §3). A publish that declares one of
* those keys now fails JSON Schema validation as an unknown property (an unknown enum value for
* `topology.mode: per-instance` and `scope: instance`), before any rule here runs. The 13 rows that
* stay are all `runtime: 'live'` and carry no `equals` (R-082 item 5). The `runtime` and `equals`
* fields, `checkRemovedSurfaces` and its rule id stay in the contract with their behaviour: a door
* still calls them, and a later row may use them again.
*/
/** Whether a runtime still reads a deprecated surface. `removed`: no data plane or control plane
*  code runs it any more (2026-10-14-app-pools-runtime-deletion, D-7), so a publish that declares it
*  deploys nothing and both doors refuse it for every app. `live`: a runtime reader remains, so the
*  grandfather rule of `checkDeprecatedSurfaceNewUse` applies. */
const DEPRECATED_SURFACE_RUNTIMES = ["removed", "live"];
/** The rule id of every warning `checkDeprecatedSurfaces` emits; the pointer in `path`
*  distinguishes the surfaces. A gate name: never renamed for prose. */
const DEPRECATED_SURFACE_RULE = "deprecated-surface";
/** The rule id of every error `checkDeprecatedSurfaceNewUse` emits (D-13). A gate name: never
*  renamed for prose. Release-engine prefixes the 400 `detail` with it, so a client can match
*  `detail.startsWith('deprecated-surface-new-use:')`. */
const DEPRECATED_SURFACE_NEW_USE_RULE = "deprecated-surface-new-use";
/** The rule id of every error `checkRemovedSurfaces` emits (2026-10-14-app-pools-runtime-deletion,
*  D-7). A gate name: never renamed for prose. Release-engine prefixes the 400 `detail` with it. */
const DEPRECATED_SURFACE_REMOVED_RULE = "deprecated-surface-removed";
const POOL = "an app-level pool in app.json `pools`";
const CLASS_AND_RESOURCES = "`class` and `resources` on the service";
const DATABASE_REPLACEMENT = "a fixture's own `infrastructure.database` (the platform derives the schema name `<appSlug>_<fixtureId>`)";
/** The inventory (WS-8 §7, schema rows): 13 rows after the 40.0.0 major, 11 fixture and 2 app.
*  Order is the order warnings print. */
const DEPRECATED_SURFACES = Object.freeze([
	{
		document: "fixture",
		pointer: "/autoscalers",
		replacement: "app.json `autoscalers`",
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/resources",
		replacement: "`infrastructure`",
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/machineType",
		replacement: CLASS_AND_RESOURCES,
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/nodeType",
		replacement: CLASS_AND_RESOURCES,
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/gpu",
		replacement: "`class: gpu-inference` or `class: gpu-training`",
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/gpuCount",
		replacement: "`class` (the machine type carries the accelerator count)",
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/accelerator",
		replacement: "`class`",
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/scalingIntent",
		replacement: `${POOL} (\`warmFloor\`, \`maxNodes\`)`,
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/sizing",
		replacement: "`resources`",
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/scaling",
		replacement: `\`topology.maxReplicas\` for the replica ceiling on the HTTP scaler path, or ${POOL} with a function-valued knob`,
		runtime: "live"
	},
	{
		document: "fixture",
		pointer: "/services/*/containers/*/gpu",
		replacement: "`class` on the service",
		runtime: "live"
	},
	{
		document: "app",
		pointer: "/databaseSchema",
		replacement: DATABASE_REPLACEMENT,
		runtime: "live"
	},
	{
		document: "app",
		pointer: "/platform/database/schema",
		replacement: DATABASE_REPLACEMENT,
		runtime: "live"
	}
]);
/** The wildcard segment: it stands for every index of an array. The only wildcard the inventory
*  uses, and the only one this resolver understands. */
const POINTER_WILDCARD = "*";
/** A plain JSON object, or `null` for anything else (an array included). Every manifest here is
*  `unknown`: at the CLI door these rules run even when schema validation failed. */
function asRecord(v) {
	return typeof v === "object" && v !== null && !Array.isArray(v) ? v : null;
}
/**
* Every place `segments` resolves to a declared value in `value`, as a concrete JSON Pointer with
* each `*` replaced by the index it matched. `prefix` is the pointer walked so far. With `equals`
* set, a leaf counts only when it is exactly that string (strict equality, no coercion).
*
* A segment that is not `*` needs a plain object to read from, and a `*` needs an array. Anything
* else resolves to nothing, so a manifest whose `services` is a string stays quiet. A key present
* with the value `null` counts as declared, because the author wrote it.
*/
function resolvePointer(value, segments, prefix, equals, out) {
	const [head, ...rest] = segments;
	if (head === void 0) {
		if (value === void 0) return;
		if (equals === void 0 || value === equals) out.push(prefix);
		return;
	}
	if (head === POINTER_WILDCARD) {
		if (!Array.isArray(value)) return;
		for (const [index, item] of value.entries()) resolvePointer(item, rest, `${prefix}/${index}`, equals, out);
		return;
	}
	const record = asRecord(value);
	if (record === null) return;
	resolvePointer(record[head], rest, `${prefix}/${head}`, equals, out);
}
/** One untrusted value as a JSON Pointer segment: sanitised by the M-1 defence
*  (`safeRuleLabel`) first, then escaped for the pointer grammar (RFC 6901 `~0`, `~1`). Local
*  rather than imported for the reason `safeRuleLabel` gives for its own local copy: exporting a
*  one-line escape would widen this package's public API for one internal caller. `app-pool-rules.ts`
*  carries the twin and `deprecated-surface-rules.test.ts` pins the two to the same output. */
function pointerSegment(raw) {
	return safeRuleLabel(raw).replaceAll("~", "~0").replaceAll("/", "~1");
}
/** Every row of `inventory` for one document kind, resolved against one manifest, in inventory
*  order. `pathPrefix` is `''` for the app manifest and `fixtures/<id>` for a fixture (the
*  convention `pool-image-fixture-unreferenced` uses). */
function surfacesIn(manifest, document, pathPrefix, inventory, out) {
	if (asRecord(manifest) === null) return;
	for (const surface of inventory) {
		if (surface.document !== document) continue;
		const resolved = [];
		resolvePointer(manifest, surface.pointer.split("/").slice(1), "", surface.equals, resolved);
		for (const pointer of resolved) out.push({
			surface,
			path: `${pathPrefix}${pointer}`
		});
	}
}
/** Every resolved row of the whole bundle: the app manifest first, then each fixture in bundle
*  order, and the inventory's order within each. */
function surfacesInBundle(bundle, inventory) {
	const out = [];
	surfacesIn(bundle.app, "app", "", inventory, out);
	for (const { fixtureId, manifest } of bundle.fixtures) surfacesIn(manifest, "fixture", `fixtures/${pointerSegment(fixtureId)}`, inventory, out);
	return out;
}
/** The lookup key of one inventory row. `document` is one of two fixed words, every pointer
*  starts with `/`, and a pointer never holds `=`, so the concatenation is unambiguous. `equals`
*  joins the key, so `mode = per-instance` and a later `mode = singleton` row never collapse. */
function surfaceKey(surface) {
	const base = `${surface.document}${surface.pointer}`;
	return surface.equals === void 0 ? base : `${base}=${surface.equals}`;
}
/** The pointer a message names: the row's template, with `= <value>` for a value row. */
function surfaceLabel(surface) {
	return surface.equals === void 0 ? surface.pointer : `${surface.pointer} = '${surface.equals}'`;
}
/**
* Emit one `warning` per deprecated surface a manifest in the bundle still declares. Pure, no I/O,
* never throws. Called at both publish doors beside `checkAppPools` (WS-8 binds the CLI door, WS-3
* the CP door), and the census reads the same inventory for its key list (I-7).
*
* Inputs: `bundle.app` is `app.json` and `bundle.fixtures` is every `fixture.json` with its id.
* All stay `unknown`, because at the CLI door these rules run when schema validation has already
* failed. A manifest that is not a plain object yields no issues for that document.
*
* Output: one `DeprecatedSurfaceWarning` per row of `DEPRECATED_SURFACES` that resolves. Every one carries
* `severity: 'warning'` and `rule` equal to `DEPRECATED_SURFACE_RULE`. `path` is the resolved
* pointer, each `*` replaced by the index it matched, prefixed `fixtures/<id>` for a fixture
* document. The message is `"<pointer> is deprecated; declare <replacement> instead"` and names the
* row's own pointer, so one surface reads the same however many array members carry it. A row that
* resolves at three array indices yields three issues with three paths and one message. The order
* is the app manifest first, then each fixture in bundle order, and the inventory's order within
* each. An empty bundle and a bundle declaring nothing deprecated both yield no issues.
*
* Invariants: every issue is a `warning`, and the return type says so (R-031). `checkAppPools` owns the all-error surface, and the two
* modules are apart so each can pin its own severity. Every pointer in the output comes from the
* inventory, so no app-authored string reaches the message. The one app-authored value in the
* output is the fixture id in `path`, sanitised by `safeRuleLabel`. Side effects: none.
*/
function checkDeprecatedSurfaces(bundle) {
	return deprecatedSurfaceWarningsIn(bundle, DEPRECATED_SURFACES);
}
/**
* `checkDeprecatedSurfaces` over a given inventory in place of `DEPRECATED_SURFACES`. Internal: the
* package root does not export it, so no app reaches it. `checkDeprecatedSurfaces` is this function
* with the real inventory, and the module's tests call it with a one-row inventory to exercise a
* value row (`equals`), which no row of the 40.0.0 inventory carries. Same inputs, outputs,
* invariants and side effects as `checkDeprecatedSurfaces`; `inventory` is read and never changed.
*/
function deprecatedSurfaceWarningsIn(bundle, inventory) {
	return surfacesInBundle(bundle, inventory).map(({ surface, path }) => ({
		severity: "warning",
		path,
		rule: DEPRECATED_SURFACE_RULE,
		message: `${surfaceLabel(surface)} is deprecated; declare ${surface.replacement} instead`
	}));
}
/**
* Refuse a deprecated surface that is new to the app (D-13, the grandfather rule). Pure, no I/O,
* never throws. Release-engine calls it at the publish door with the surfaces that the app's
* earlier versions declared, and throws on the first issue as a 400 whose `detail` begins
* `deprecated-surface-new-use:`. The CLI door does not call it: the CLI holds no version history,
* so it keeps the `checkDeprecatedSurfaces` warning alone.
*
* Inputs:
* - `bundle`: the manifests of the version under publish, the same `AppBundleManifests` that
*   `checkDeprecatedSurfaces` reads. Every manifest stays `unknown`; a manifest that is not a plain
*   object yields no issues for that document.
* - `declaredBefore`: the inventory rows that any earlier version of the same app declared, each
*   as its `document` and its `pointer` template exactly as `DEPRECATED_SURFACES` spells it (with
*   `*` for an array index), and its `equals` when the row has one. This is the census key shape
*   (`manifestCensusQuerySchema`: `key`, and `value` for `equals`). The key is
*   app-wide: a fixture row that one fixture of the app declared before admits that row in every
*   fixture of the app. An empty list means a new app, or an app whose earlier versions declared
*   no deprecated surface. Order and duplicates do not matter.
*
* Output: one `DeprecatedSurfaceError` per resolved hit of a row whose key is absent from
* `declaredBefore`, with the hits of admitted rows left out. A `removed` row can be refused here
* and by `checkRemovedSurfaces`; a door must run `checkRemovedSurfaces` first, so the 400 always
* names `deprecated-surface-removed` for a removed surface. Every issue carries `severity:
* 'error'` and `rule` equal to `DEPRECATED_SURFACE_NEW_USE_RULE`. `path` is the hit's concrete
* pointer with the same prefix convention as `checkDeprecatedSurfaces`. The message is
* `"<pointer> is deprecated and no earlier version of this app declares it; declare <replacement>
* instead"`, with the row's own template pointer, so it always names the replacement. The order
* is the order of `checkDeprecatedSurfaces`. A bundle that declares no deprecated surface, or only
* admitted ones, yields no issues.
*
* Narrow by design (defaults 1 and 2 of the feature index):
* - An entry of `declaredBefore` admits only a row with the identical `document`, template
*   `pointer` and `equals` (both absent, or both the same string). A concrete pointer such as `/services/0/machineType` admits nothing, and neither does
*   an app key spelled for a fixture or the reverse.
* - An entry that names no inventory row admits nothing and is not an error.
*
* Invariants: every issue is an `error`, and the return type says so. The set of paths in the
* output is always a subset of the paths `checkDeprecatedSurfaces` returns for the same bundle.
* Every pointer in a message comes from the inventory, so no app-authored string reaches the
* message; the fixture id in `path` passes through `safeRuleLabel` and the RFC 6901 escape.
* Side effects: none; `declaredBefore` is read and never changed.
*/
function checkDeprecatedSurfaceNewUse(bundle, declaredBefore) {
	return deprecatedSurfaceNewUseIn(bundle, declaredBefore, DEPRECATED_SURFACES);
}
/**
* `checkDeprecatedSurfaceNewUse` over a given inventory in place of `DEPRECATED_SURFACES`.
* Internal, as `deprecatedSurfaceWarningsIn` is: the package root does not export it. The module's
* tests call it with a one-row inventory to exercise a value row. Same inputs, outputs, invariants
* and side effects as `checkDeprecatedSurfaceNewUse`.
*/
function deprecatedSurfaceNewUseIn(bundle, declaredBefore, inventory) {
	const admitted = new Set(declaredBefore.map(surfaceKey));
	return surfacesInBundle(bundle, inventory).filter(({ surface }) => !admitted.has(surfaceKey(surface))).map(({ surface, path }) => ({
		severity: "error",
		path,
		rule: DEPRECATED_SURFACE_NEW_USE_RULE,
		message: `${surfaceLabel(surface)} is deprecated and no earlier version of this app declares it; declare ${surface.replacement} instead`
	}));
}
/**
* Refuse every surface that no runtime reads any more, for every app (2026-10-14-app-pools-runtime-
* deletion, D-7). Pure, no I/O, never throws. Both publish doors call it: release-engine throws on
* the first issue as a 400 whose `detail` begins `deprecated-surface-removed:`, and `zion-app
* validate` reports each issue as an error. It holds no version history, because the grandfather
* rule does not apply: a grandfathered app that declares a removed surface would deploy something
* that never runs.
*
* Output: one `DeprecatedSurfaceError` per resolved hit of a row whose `runtime` is `removed`, in
* the order of `checkDeprecatedSurfaces`, with `rule` equal to `DEPRECATED_SURFACE_REMOVED_RULE`.
* The message is `"<pointer> is no longer run by the platform; declare <replacement> instead"`.
* Every pointer in a message comes from the inventory; the fixture id in `path` passes through
* `safeRuleLabel` and the RFC 6901 escape. The paths are a subset of `checkDeprecatedSurfaces`.
*
* From the 40.0.0 major no row of `DEPRECATED_SURFACES` is `removed`: R2 deleted the seven
* `removed` rows with their schema keys (R-081), so JSON Schema validation refuses those keys at
* both doors before this rule runs. Until a row is `removed` again, the function returns no issue
* for any bundle.
*/
function checkRemovedSurfaces(bundle) {
	return removedSurfaceErrorsIn(bundle, DEPRECATED_SURFACES);
}
/**
* `checkRemovedSurfaces` over a given inventory in place of `DEPRECATED_SURFACES`. Internal, as
* `deprecatedSurfaceWarningsIn` is: the package root does not export it. The module's tests call it
* with a one-row `removed` inventory, because the 40.0.0 inventory holds no such row. Same inputs,
* outputs, invariants and side effects as `checkRemovedSurfaces`.
*/
function removedSurfaceErrorsIn(bundle, inventory) {
	return surfacesInBundle(bundle, inventory).filter(({ surface }) => surface.runtime === "removed").map(({ surface, path }) => ({
		severity: "error",
		path,
		rule: DEPRECATED_SURFACE_REMOVED_RULE,
		message: `${surfaceLabel(surface)} is no longer run by the platform; declare ${surface.replacement} instead`
	}));
}
//#endregion
export { DEPRECATED_SURFACES, DEPRECATED_SURFACE_NEW_USE_RULE, DEPRECATED_SURFACE_REMOVED_RULE, DEPRECATED_SURFACE_RULE, DEPRECATED_SURFACE_RUNTIMES, checkDeprecatedSurfaceNewUse, checkDeprecatedSurfaces, checkRemovedSurfaces, deprecatedSurfaceNewUseIn, deprecatedSurfaceWarningsIn, removedSurfaceErrorsIn };

//# sourceMappingURL=deprecated-surface-rules.js.map